import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, CSSProperties } from 'react';
import {
  format,
  parseISO,
  startOfDay,
  eachDayOfInterval,
  differenceInCalendarDays,
  addDays,
  subDays,
  isValid,
} from 'date-fns';
import zhCN from 'date-fns/locale/zh-CN';
import { ChevronLeft, ChevronRight, Circle, Flag, List, Locate } from 'lucide-react';
import type { ScheduleVersion, Milestone, ScheduleMilestone } from '../types';
import { formatSpecRowTitle } from '../utils/specDisplay';
import { IpcEmpty } from './ui';
import './ScheduleGanttChart.css';
import { milestoneStatusLabel } from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';

type GanttViewMode = 'day' | 'week' | 'month';

const VIEW_MODE_STORAGE_KEY = 'ipc_gantt_view_mode';

const VIEW_COL_W: Record<GanttViewMode, number> = {
  day: 28,
  week: 80,
  month: 120,
};

/** 左侧堆叠列宽（原型约 180px） */
const LEFT_W = 200;

const WEEK_ZH = ['日', '一', '二', '三', '四', '五', '六'];

export interface ScheduleGanttChartProps {
  schedule: ScheduleVersion | null;
  /** 仅当工作日历中有该日且为非工作日时返回 true */
  isNonWorkingDay: (date: Date) => boolean;
  /** 拖拽里程碑条后回写日期（精确到天），返回 Promise 表示异步保存 */
  onMilestoneDrag?: (specId: number, milestoneId: number, newStartDate: string, newEndDate: string) => Promise<void>;
}

interface RowGroup {
  key: string;
  specId: string;
  milestoneId: string;
  specName: string;
  milestoneName: string;
  status: ScheduleMilestone['status'];
  planStart: string;
  planEnd: string;
  actualStart: string;
  actualEnd: string;
}

function sortedColumns(schedule: ScheduleVersion): Milestone[] {
  const c = schedule.milestoneColumns;
  if (!c?.length) return [];
  return [...c].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function safeParseDay(s?: string): Date | null {
  if (!s?.trim()) return null;
  const d = parseISO(s.trim());
  return isValid(d) ? startOfDay(d) : null;
}

/** 当年 MM-dd，跨年 yyyy-MM-dd */
function formatShortDate(raw?: string): string {
  if (!raw?.trim()) return '';
  const d = safeParseDay(raw);
  if (!d) return raw.trim();
  const currentYear = new Date().getFullYear();
  return d.getFullYear() !== currentYear ? format(d, 'yyyy-MM-dd') : format(d, 'MM-dd');
}

function formatPlanMeta(planStart: string, planEnd: string): string | null {
  const s = formatShortDate(planStart);
  const e = formatShortDate(planEnd);
  if (!s && !e) return null;
  if (s && e) return `计划 ${s} ~ ${e}`;
  return `计划 ${s || e}`;
}

function formatActualMeta(row: Pick<RowGroup, 'actualStart' | 'actualEnd' | 'status'>): string | null {
  if (!row.actualStart && !row.actualEnd) return null;
  const s = formatShortDate(row.actualStart);
  const e = formatShortDate(row.actualEnd);
  if (row.status === 'in_progress') {
    return s ? `实际 ${s} ~ 进行中` : '实际 进行中';
  }
  if (s && e) return `实际 ${s} ~ ${e}`;
  if (s || e) return `实际 ${s || e}`;
  return null;
}

function buildMonthSegments(days: Date[]): { label: string; count: number; key: string }[] {
  const segments: { label: string; count: number; key: string }[] = [];
  let i = 0;
  while (i < days.length) {
    const d0 = days[i]!;
    const ym = format(d0, 'yyyy-MM');
    const label = format(d0, 'yyyy年M月', { locale: zhCN });
    let j = i + 1;
    while (j < days.length && format(days[j]!, 'yyyy-MM') === ym) j++;
    segments.push({ label, count: j - i, key: ym });
    i = j;
  }
  return segments;
}

/** ISO week number (1-53) */
function getISOWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/** Build week-level columns from day range */
function buildWeekColumns(days: Date[]): Array<{ key: string; label: string; startIdx: number; count: number }> {
  if (!days.length) return [];
  const cols: Array<{ key: string; label: string; startIdx: number; count: number }> = [];
  let i = 0;
  while (i < days.length) {
    const d0 = days[i]!;
    const yw = `${format(d0, 'yyyy')}-W${String(getISOWeek(d0)).padStart(2, '0')}`;
    const label = `${format(d0, 'M/d')} W${getISOWeek(d0)}`;
    let j = i + 1;
    while (j < days.length && `${format(days[j]!, 'yyyy')}-W${String(getISOWeek(days[j]!)).padStart(2, '0')}` === yw) j++;
    cols.push({ key: yw, label, startIdx: i, count: j - i });
    i = j;
  }
  return cols;
}

/** Build month-level columns from day range */
function buildMonthColumns(days: Date[]): Array<{ key: string; label: string; startIdx: number; count: number }> {
  if (!days.length) return [];
  const cols: Array<{ key: string; label: string; startIdx: number; count: number }> = [];
  let i = 0;
  while (i < days.length) {
    const d0 = days[i]!;
    const ym = format(d0, 'yyyy-MM');
    const label = format(d0, 'yyyy年M月', { locale: zhCN });
    let j = i + 1;
    while (j < days.length && format(days[j]!, 'yyyy-MM') === ym) j++;
    cols.push({ key: ym, label, startIdx: i, count: j - i });
    i = j;
  }
  return cols;
}

/** Compute bar position in week or month view */
function barRectAggregated(
  startStr: string,
  endStr: string,
  columns: Array<{ startIdx: number; count: number }>,
  days: Date[],
  timelineStart: Date,
  colW: number,
): { left: number; width: number } | null {
  const s = safeParseDay(startStr);
  const e = safeParseDay(endStr);
  if (!s || !e) return null;
  if (e.getTime() < s.getTime()) return null;
  const startDayIdx = differenceInCalendarDays(s, timelineStart);
  const endDayIdx = differenceInCalendarDays(e, timelineStart);
  if (endDayIdx < 0 || startDayIdx >= days.length) return null;
  const clampedStart = Math.max(0, startDayIdx);
  const clampedEnd = Math.min(days.length - 1, endDayIdx);
  let leftCol = 0;
  for (let c = 0; c < columns.length; c++) {
    const col = columns[c]!;
    if (clampedStart >= col.startIdx && clampedStart < col.startIdx + col.count) {
      const offsetInCol = clampedStart - col.startIdx;
      const ratio = offsetInCol / col.count;
      leftCol = c + ratio;
      break;
    }
    if (clampedStart >= col.startIdx + col.count) {
      leftCol = c + 1;
    }
  }
  let rightCol = 0;
  for (let c = 0; c < columns.length; c++) {
    const col = columns[c]!;
    if (clampedEnd >= col.startIdx && clampedEnd < col.startIdx + col.count) {
      const offsetInCol = clampedEnd - col.startIdx + 1;
      const ratio = offsetInCol / col.count;
      rightCol = c + ratio;
      break;
    }
    if (clampedEnd >= col.startIdx + col.count) {
      rightCol = c + 1;
    }
  }
  const left = leftCol * colW;
  const width = Math.max((rightCol - leftCol) * colW, colW * 0.3);
  return { left, width };
}

function buildDependencyPairs(schedule: ScheduleVersion): Array<{ from: string; to: string }> {
  const rels = schedule.milestoneRelations ?? [];
  const out: Array<{ from: string; to: string }> = [];
  const dedupe = new Set<string>();
  for (const r of rels) {
    const current = String(r.milestoneId ?? '');
    if (!current) continue;
    if (r.parentMilestoneId != null && String(r.parentMilestoneId).trim()) {
      const from = String(r.parentMilestoneId);
      const to = current;
      const key = `${from}->${to}`;
      if (!dedupe.has(key)) {
        dedupe.add(key);
        out.push({ from, to });
      }
    }
    if (r.nextMilestoneId != null && String(r.nextMilestoneId).trim()) {
      const from = current;
      const to = String(r.nextMilestoneId);
      const key = `${from}->${to}`;
      if (!dedupe.has(key)) {
        dedupe.add(key);
        out.push({ from, to });
      }
    }
  }
  return out;
}

function buildRowGroups(schedule: ScheduleVersion): RowGroup[] {
  const cols = sortedColumns(schedule);
  const out: RowGroup[] = [];
  for (const spec of schedule.specSchedules ?? []) {
    for (const col of cols) {
      const sm = spec.milestones.find(m => String(m.milestoneId) === String(col.id));
      out.push({
        key: `${spec.id}-${col.id}`,
        specId: String(spec.id),
        milestoneId: String(col.id),
        specName: spec.specName ?? '',
        milestoneName: col.name ?? col.id,
        status: sm?.status ?? 'pending',
        planStart: (sm?.startDate && String(sm.startDate).trim()) || '',
        planEnd: (sm?.endDate && String(sm.endDate).trim()) || '',
        actualStart: (sm?.actualStartDate && String(sm.actualStartDate).trim()) || '',
        actualEnd: (sm?.actualEndDate && String(sm.actualEndDate).trim()) || '',
      });
    }
  }
  return out;
}

/**
 * 关键路径（忽略依赖）：只看计划起止。
 * 扫时间轴上每个覆盖变化点，在「覆盖到该日」的节点中取 planEnd 最大的一批（同结束日并行），
 * 并集即为关键路径。例：F→A/B(并行)→E→Y。
 */
function buildCriticalPathMilestoneIds(rows: RowGroup[]): Set<string> {
  const MS_DAY = 86400000;
  type Interval = { id: string; start: number; end: number };
  const intervals: Interval[] = [];
  for (const r of rows) {
    const s = safeParseDay(r.planStart);
    const e = safeParseDay(r.planEnd);
    if (!s || !e) continue;
    if (e.getTime() < s.getTime()) continue;
    intervals.push({ id: r.milestoneId, start: s.getTime(), end: e.getTime() });
  }
  if (intervals.length === 0) return new Set();

  // 事件点：区间开始、结束次日（半开区间离开点），保证 [start, end] 闭区间覆盖判断正确
  const eventTimes = new Set<number>();
  for (const iv of intervals) {
    eventTimes.add(iv.start);
    eventTimes.add(iv.end + MS_DAY);
  }
  const sortedTimes = [...eventTimes].sort((a, b) => a - b);

  const criticalIds = new Set<string>();
  for (let i = 0; i < sortedTimes.length - 1; i++) {
    const t = sortedTimes[i]!;
    const active = intervals.filter(iv => iv.start <= t && iv.end >= t);
    if (active.length === 0) continue;
    const maxEnd = Math.max(...active.map(iv => iv.end));
    for (const iv of active) {
      if (iv.end === maxEnd) criticalIds.add(iv.id);
    }
  }
  return criticalIds;
}

function collectDateBounds(groups: RowGroup[]): { min: Date; max: Date } | null {
  let minT = Infinity;
  let maxT = -Infinity;
  const bump = (s?: string) => {
    const d = safeParseDay(s);
    if (!d) return;
    const t = d.getTime();
    minT = Math.min(minT, t);
    maxT = Math.max(maxT, t);
  };
  for (const g of groups) {
    bump(g.planStart);
    bump(g.planEnd);
    bump(g.actualStart);
    bump(g.actualEnd);
  }
  if (minT === Infinity) return null;
  return { min: new Date(minT), max: new Date(maxT) };
}

/** 判定节点是否延期：状态非 completed 且计划结束日期 < 当天 */
function isOverdue(row: RowGroup, today: Date): boolean {
  if (row.status === 'completed') return false;
  const planEnd = safeParseDay(row.planEnd);
  if (!planEnd) return false;
  return planEnd < startOfDay(today);
}

type TimeStatus = { kind: 'normal' | 'delay' | 'early'; days: number };

/** 时间状态标签：完成看实际 vs 计划；未完成看计划结束 vs 今天 */
function getTimeStatus(row: RowGroup, today: Date): TimeStatus {
  const planEnd = safeParseDay(row.planEnd);
  if (!planEnd) return { kind: 'normal', days: 0 };

  if (row.status === 'completed') {
    const actualEnd = safeParseDay(row.actualEnd) ?? safeParseDay(row.actualStart);
    if (!actualEnd) return { kind: 'normal', days: 0 };
    const delta = differenceInCalendarDays(actualEnd, planEnd);
    if (delta > 0) return { kind: 'delay', days: delta };
    if (delta < 0) return { kind: 'early', days: -delta };
    return { kind: 'normal', days: 0 };
  }

  if (planEnd < startOfDay(today)) {
    return { kind: 'delay', days: differenceInCalendarDays(today, planEnd) };
  }
  return { kind: 'normal', days: 0 };
}

function statusLabel(status: ScheduleMilestone['status']): string {
  return milestoneStatusLabel(status);
}

function TimelineRow({
  totalW,
  colW,
  dayBgs,
  bar,
  variant,
  status,
  planAsGhost = false,
  compact = false,
  overdue = false,
  barRefKey,
  onBarRef,
  draggable = false,
  onBarMouseDown,
  dragPreview,
  ghostBar,
  dragDateLabel,
}: {
  totalW: number;
  colW: number;
  dayBgs: Array<{ key: string; nonWork: boolean; w: number }>;
  bar: { left: number; width: number } | null;
  variant: 'plan' | 'actual';
  status: ScheduleMilestone['status'];
  /** 有实际条时，计划条显示为虚线「计划」 */
  planAsGhost?: boolean;
  compact?: boolean;
  overdue?: boolean;
  barRefKey?: string;
  onBarRef?: (key: string, node: HTMLDivElement | null) => void;
  draggable?: boolean;
  onBarMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  dragPreview?: { left: number; width: number } | null;
  ghostBar?: { left: number; width: number } | null;
  dragDateLabel?: string | null;
}) {
  const barClass = [
    'schedule-gantt-bar',
    variant === 'plan' ? 'schedule-gantt-bar-plan' : 'schedule-gantt-bar-actual',
    `schedule-gantt-bar--${status}`,
    planAsGhost ? 'schedule-gantt-bar--ghost-plan' : '',
    compact ? 'compact' : '',
    overdue ? 'schedule-gantt-bar-overdue' : '',
    draggable ? 'schedule-gantt-bar-draggable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`schedule-gantt-tl-row${compact ? ' compact' : ''}`}
      style={{ width: totalW, ['--gantt-day-w' as string]: `${colW}px` } as CSSProperties}
    >
      <div className="schedule-gantt-tl-row-bg" style={{ display: 'flex', width: totalW }}>
        {dayBgs.map(bg => (
          <div
            key={bg.key}
            className={`schedule-gantt-day-bg${bg.nonWork ? ' non-work' : ''}`}
            style={{ width: bg.w, flexShrink: 0 }}
          />
        ))}
      </div>
      <div className={`schedule-gantt-bar-layer${compact ? ' compact' : ''}`}>
        {bar && (
          <>
            {ghostBar && (
              <div
                className="schedule-gantt-bar schedule-gantt-bar--ghost-plan schedule-gantt-ghost-bar"
                style={{ left: ghostBar.left, width: ghostBar.width }}
              />
            )}
            <div
              className={barClass}
              style={dragPreview ? { left: dragPreview.left, width: dragPreview.width } : { left: bar.left, width: bar.width }}
              ref={node => {
                if (barRefKey && onBarRef) onBarRef(barRefKey, node);
              }}
              onMouseDown={draggable ? onBarMouseDown : undefined}
            />
            {dragPreview && dragDateLabel && (
              <div
                className="schedule-gantt-drag-tooltip"
                style={{ left: dragPreview.left + dragPreview.width + 6 }}
              >
                {dragDateLabel}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Convert pixel offset to calendar day delta in week/month views.
 * In day view: colW=28px, 1 col = 1 day.
 * In week view: colW=80px, 1 col = 1 week; maps through weekColumns.
 * In month view: colW=120px, 1 col = 1 month; maps through monthColumns.
 */
function pixelToDayDelta(
  pixelDx: number,
  viewMode: GanttViewMode,
  colW: number,
  startDateStr: string,
  days: Date[],
  _weekColumns: Array<{ startIdx: number; count: number }>,
  _monthColumns: Array<{ startIdx: number; count: number }>,
): number {
  // pixelDx is snapped to current view's per-day px, compute dayDelta
  const daysPerCol = viewMode === 'week' ? 7 : viewMode === 'month' ? 30 : 1;
  const dayPx = colW / daysPerCol;
  const dayDelta = Math.round(pixelDx / dayPx);
  if (dayDelta === 0) return 0;
  if (viewMode === 'day') return dayDelta;

  const s = safeParseDay(startDateStr);
  if (!s) return dayDelta;

  // Clamp to valid day range
  const targetIdx = days.indexOf(s) !== -1 
    ? days.indexOf(s) + dayDelta 
    : differenceInCalendarDays(s, days[0]!) + dayDelta;
  const clamped = Math.max(0, Math.min(days.length - 1, targetIdx));
  const targetDate = days[clamped]!;
  return Math.round((targetDate.getTime() - s.getTime()) / 86400000);
}

export const ScheduleGanttChart: FC<ScheduleGanttChartProps> = ({
 schedule, isNonWorkingDay, onMilestoneDrag }) => {
  useIpcStatusEnums();
  const scrollRef = useRef<HTMLDivElement>(null);
  const ganttBodyRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevScrollRatio = useRef<number>(0);
  const [dependencySegments, setDependencySegments] = useState<Array<{ points: string; arrow: string; kind: 'plan' | 'actual' }>>([]);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const [selectedSpecId, setSelectedSpecId] = useState<string>('');
  const [showCriticalPathOnly, setShowCriticalPathOnly] = useState(false);
  const [viewMode, setViewMode] = useState<GanttViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'day' || stored === 'week' || stored === 'month') return stored;
    } catch { /* localStorage 不可用 */ }
    return 'day';
  });

  // ---- 拖拽状态 ----
  const dragState = useRef<{
    specId: number;
    milestoneId: number;
    startDate: string;
    endDate: string;
    durationDays: number;
    startX: number;
    origLeft: number;
    origWidth: number;
    barEl: HTMLDivElement;
    dragging: boolean;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ left: number; width: number } | null>(null);

  const handleBarMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>, row: RowGroup & { planBar: { left: number; width: number } | null }) => {
    if (!onMilestoneDrag) return;
    if (!row.planBar) return;
    const s = safeParseDay(row.planStart);
    const end = safeParseDay(row.planEnd);
    if (!s || !end) return;
    const duration = differenceInCalendarDays(end, s) + 1;
    dragState.current = {
      specId: Number(row.specId),
      milestoneId: Number(row.milestoneId),
      startDate: row.planStart,
      endDate: row.planEnd,
      durationDays: duration,
      startX: e.clientX,
      origLeft: row.planBar.left,
      origWidth: row.planBar.width,
      barEl: e.currentTarget,
      dragging: false,
    };
    setDragPreview({ left: row.planBar.left, width: row.planBar.width });
    e.preventDefault();
    e.stopPropagation();
  }, [onMilestoneDrag]);

  useEffect(() => {
    if (!onMilestoneDrag || !dragPreview) return;
    const handleMove = (e: MouseEvent) => {
      const ds = dragState.current;
      if (!ds) return;
      const dx = e.clientX - ds.startX;
      // 吸附到天：按当前视图列宽对齐
      const colW = VIEW_COL_W[viewMode];
      // Per-day px in current view: day=28px, week=80/7≈11.4px, month=120/30=4px
      const daysPerCol = viewMode === 'week' ? 7 : viewMode === 'month' ? 30 : 1;
      const dayPx = colW / daysPerCol;
      const dayDelta = Math.round(dx / dayPx);
      const snapPx = dayDelta * dayPx;
      const newLeft = ds.origLeft + snapPx;
      if (Math.abs(dx) > 2) {
        ds.dragging = true;
      }
      setDragPreview({ left: newLeft, width: ds.origWidth });
    };
    const handleUp = async (e: MouseEvent) => {
      const ds = dragState.current;
      // Keep preview visible during API call to prevent visual "jump back"
      if (!ds || !ds.dragging) { dragState.current = null; setDragPreview(null); return; }
      const dx = e.clientX - ds.startX;
      // Snap to day grid (28px) so drag translates to day-by-day movement
      const colW = VIEW_COL_W[viewMode];
      const daysPerCol = viewMode === 'week' ? 7 : viewMode === 'month' ? 30 : 1;
      const dayPx = colW / daysPerCol;
      const dayDeltaRaw = Math.round(dx / dayPx);
      const snappedDx = dayDeltaRaw * dayPx;
      const dayDelta = pixelToDayDelta(snappedDx, viewMode, colW, ds.startDate, days, weekColumns, monthColumns);
      if (dayDelta === 0) { dragState.current = null; setDragPreview(null); return; }
      const s = safeParseDay(ds.startDate);
      if (!s) { dragState.current = null; setDragPreview(null); return; }
      const newStart = addDays(s, dayDelta);
      const newEnd = addDays(newStart, ds.durationDays - 1);
      const newStartStr = format(newStart, 'yyyy-MM-dd');
      const newEndStr = format(newEnd, 'yyyy-MM-dd');
      try {
        await onMilestoneDrag(ds.specId, ds.milestoneId, newStartStr, newEndStr);
      } catch {
        // 拖拽失败静默处理，不回滚 UI（由父组件刷新数据）
      } finally {
        dragState.current = null;
        setDragPreview(null);
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [onMilestoneDrag, dragPreview, viewMode]);

  const allRowGroups = useMemo(() => (schedule ? buildRowGroups(schedule) : []), [schedule]);

  useEffect(() => {
    if (!schedule?.specSchedules?.length) {
      setSelectedSpecId('');
      return;
    }
    const currentExists = schedule.specSchedules.some(s => String(s.id) === selectedSpecId);
    if (!currentExists) {
      setSelectedSpecId(String(schedule.specSchedules[0].id));
    }
  }, [schedule, selectedSpecId]);

  const rowGroups = useMemo(() => {
    if (!selectedSpecId) return [];
    return allRowGroups.filter(g => g.specId === selectedSpecId);
  }, [allRowGroups, selectedSpecId]);

  const criticalPathMilestoneIds = useMemo(() => {
    if (!rowGroups.length) return new Set<string>();
    return buildCriticalPathMilestoneIds(rowGroups);
  }, [rowGroups]);

  const visibleRowGroups = useMemo(() => {
    if (!showCriticalPathOnly) return rowGroups;
    return rowGroups.filter(r => criticalPathMilestoneIds.has(r.milestoneId));
  }, [rowGroups, showCriticalPathOnly, criticalPathMilestoneIds]);

  const { days, timelineStart } = useMemo(() => {
    const fallback = () => {
      const today = startOfDay(new Date());
      const d = eachDayOfInterval({ start: subDays(today, 7), end: addDays(today, 37) });
      return { days: d, timelineStart: d[0] };
    };
    const today = startOfDay(new Date());

    if (!allRowGroups.length) return fallback();
    const bounds = collectDateBounds(allRowGroups);

    if (viewMode === 'day') {
      // 日视图：≈1个月范围，以当天为中心
      let start = subDays(today, 15);
      let end = addDays(today, 15);
      // 如果数据范围超出默认范围，取并集
      if (bounds) {
        if (bounds.min < start) start = bounds.min;
        if (bounds.max > end) end = bounds.max;
      }
      start = subDays(start, 3);
      end = addDays(end, 3);
      const d = eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
      return { days: d, timelineStart: d[0] };
    }

    if (viewMode === 'week') {
      // 周视图：≈3个月范围，以当天所在周为中心
      let start = subDays(today, 45);
      let end = addDays(today, 45);
      if (bounds) {
        if (bounds.min < start) start = bounds.min;
        if (bounds.max > end) end = bounds.max;
      }
      start = subDays(start, 7);
      end = addDays(end, 7);
      const d = eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
      return { days: d, timelineStart: d[0] };
    }

    // 月视图：≈12个月范围，以当天所在月为中心
    let start = subDays(today, 180);
    let end = addDays(today, 185);
    if (bounds) {
      if (bounds.min < start) start = bounds.min;
      if (bounds.max > end) end = bounds.max;
    }
    start = subDays(start, 15);
    end = addDays(end, 15);
    const d = eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
    return { days: d, timelineStart: d[0] };
  }, [allRowGroups, viewMode]);

  const monthSegments = useMemo(() => buildMonthSegments(days), [days]);

  const weekColumns = useMemo(() => buildWeekColumns(days), [days]);
  const monthColumns = useMemo(() => buildMonthColumns(days), [days]);

  const colW = VIEW_COL_W[viewMode];
  const totalCols = viewMode === 'day' ? days.length : viewMode === 'week' ? weekColumns.length : monthColumns.length;
  const totalWidth = LEFT_W + totalCols * colW;

  // Build background cells for timeline rows based on view mode
  const dayBgs = useMemo(() => {
    if (viewMode === 'day') {
      return days.map(d => ({
        key: format(d, 'yyyy-MM-dd'),
        nonWork: isNonWorkingDay(d),
        w: colW,
      }));
    }
    if (viewMode === 'week') {
      return weekColumns.map(col => ({
        key: col.key,
        nonWork: false,
        w: colW,
      }));
    }
    return monthColumns.map(col => ({
      key: col.key,
      nonWork: false,
      w: colW,
    }));
  }, [viewMode, days, weekColumns, monthColumns, colW, isNonWorkingDay]);

  const barRectDay = (startStr: string, endStr: string): { left: number; width: number } | null => {
    const s = safeParseDay(startStr);
    const e = safeParseDay(endStr);
    if (!s || !e) return null;
    if (e.getTime() < s.getTime()) return null;
    const left = differenceInCalendarDays(s, timelineStart) * colW;
    const width = (differenceInCalendarDays(e, s) + 1) * colW;
    return { left, width };
  };

  const barRectFn = (startStr: string, endStr: string): { left: number; width: number } | null => {
    if (viewMode === 'day') return barRectDay(startStr, endStr);
    const cols = viewMode === 'week' ? weekColumns : monthColumns;
    return barRectAggregated(startStr, endStr, cols, days, timelineStart, colW);
  };

  const layoutRows = useMemo(
    () => {
      const today = startOfDay(new Date());
      return visibleRowGroups.map(g => {
        const hasActual = !!(g.actualStart || g.actualEnd);
        const planBar = barRectFn(g.planStart, g.planEnd);
        const actualBar = hasActual ? barRectFn(g.actualStart, g.actualEnd) : null;
        const overdue = isOverdue(g, today);
        return { ...g, hasActual, planBar, actualBar, overdue };
      });
    },
    [visibleRowGroups, timelineStart, viewMode, weekColumns, monthColumns, colW],
  );

  const setBarRef = (key: string, node: HTMLDivElement | null) => {
    const map = barRefs.current;
    if (node) map.set(key, node);
    else map.delete(key);
  };

  useLayoutEffect(() => {
    if (!schedule || !ganttBodyRef.current) {
      setDependencySegments([]);
      setOverlayHeight(0);
      return;
    }
    const recalc = () => {
      const surface = ganttBodyRef.current;
      if (!surface) return;
      const surfaceRect = surface.getBoundingClientRect();
      const deps = buildDependencyPairs(schedule);
      const specs = [...new Set(layoutRows.map(r => r.specId))];
      const segments: Array<{ points: string; arrow: string; kind: 'plan' | 'actual' }> = [];
      const readBarGeom = (key: string) => {
        const el = barRefs.current.get(key);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return {
          xStart: r.left - surfaceRect.left,
          xEnd: r.right - surfaceRect.left,
          y: r.top - surfaceRect.top + r.height / 2,
        };
      };
      const pushConnector = (
        from: { xStart: number; xEnd: number; y: number },
        to: { xStart: number; xEnd: number; y: number },
        kind: 'plan' | 'actual',
      ) => {
        const sx = from.xEnd;
        const ex = to.xStart;
        const lead = 10;
        const mx = ex >= sx ? sx + Math.max(lead, (ex - sx) / 2) : sx + lead;
        const arrowBaseX = ex - 6;
        const points = `${sx},${from.y} ${mx},${from.y} ${mx},${to.y} ${arrowBaseX},${to.y}`;
        const arrow = `${arrowBaseX},${to.y - 4} ${ex},${to.y} ${arrowBaseX},${to.y + 4}`;
        segments.push({ points, arrow, kind });
      };
      for (const specId of specs) {
        for (const dep of deps) {
          const fromPlan = readBarGeom(`${specId}:${dep.from}:plan`);
          const toPlan = readBarGeom(`${specId}:${dep.to}:plan`);
          if (fromPlan && toPlan) pushConnector(fromPlan, toPlan, 'plan');
          const fromActual = readBarGeom(`${specId}:${dep.from}:actual`);
          const toActual = readBarGeom(`${specId}:${dep.to}:actual`);
          if (fromActual && toActual) pushConnector(fromActual, toActual, 'actual');
        }
      }
      setOverlayHeight(surface.scrollHeight);
      setDependencySegments(segments);
    };
    const raf = requestAnimationFrame(recalc);
    const raf2 = requestAnimationFrame(recalc);
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => recalc());
      ro.observe(ganttBodyRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [schedule, layoutRows, days.length, totalWidth]);

  // 粒度切换后恢复滚动位置，或首次加载默认定位到当天
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || days.length === 0) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll > 0 && prevScrollRatio.current > 0) {
      el.scrollLeft = prevScrollRatio.current * maxScroll;
      prevScrollRatio.current = 0;
    } else {
      // 首次加载：定位到当天
      const today = startOfDay(new Date());
      const todayIdx = differenceInCalendarDays(today, timelineStart);
      if (todayIdx >= 0 && todayIdx < days.length) {
        el.scrollLeft = Math.max(0, todayIdx * colW - 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, days.length]);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const viewModeLabels: Record<GanttViewMode, string> = { day: '日', week: '周', month: '月' };

  if (!schedule) {
    return (
      <div className="ipc-empty-host schedule-gantt-empty-wrap">
        <IpcEmpty title="请选择版本" description="在左侧「版本历史」中选择一个版本，即可查看该版本的甘特图。" />
      </div>
    );
  }

  const cols = sortedColumns(schedule);
  if (!cols.length) {
    return (
      <div className="ipc-empty-host schedule-gantt-empty-wrap">
        <IpcEmpty title="暂无里程碑列" description="请先在「数据」页配置显示列。" />
      </div>
    );
  }

  if (!(schedule.specSchedules?.length ?? 0)) {
    return (
      <div className="ipc-empty-host schedule-gantt-empty-wrap">
        <IpcEmpty title="暂无规格排期" description="当前版本暂无规格排期数据。" />
      </div>
    );
  }

  const cssVars = { ['--gantt-day-w' as string]: `${colW}px`, ['--left-w' as string]: `${LEFT_W}px` } as CSSProperties;

  const rangeLabel =
    days.length > 0
      ? `${format(timelineStart, 'yyyy年M月', { locale: zhCN })} ~ ${format(days[days.length - 1]!, 'yyyy年M月', { locale: zhCN })}`
      : '—';

  const scrollToToday = () => {
    const el = scrollRef.current;
    if (!el || !days.length) return;
    const today = startOfDay(new Date());
    let idx = 0;
    if (viewMode === 'day') {
      idx = Math.max(0, Math.min(days.length - 1, differenceInCalendarDays(today, timelineStart)));
    } else {
      const columns = viewMode === 'week' ? weekColumns : monthColumns;
      const found = columns.findIndex(c => {
        const start = days[c.startIdx]!;
        const end = days[Math.min(days.length - 1, c.startIdx + c.count - 1)]!;
        return today >= start && today <= end;
      });
      idx = found >= 0 ? found : 0;
    }
    el.scrollLeft = Math.max(0, idx * colW - 100);
  };

  const todayMarkerLeft = (() => {
    const today = startOfDay(new Date());
    if (viewMode === 'day') {
      const idx = differenceInCalendarDays(today, timelineStart);
      if (idx < 0 || idx >= days.length) return null;
      return LEFT_W + idx * colW + colW / 2;
    }
    const columns = viewMode === 'week' ? weekColumns : monthColumns;
    const found = columns.findIndex(c => {
      const start = days[c.startIdx]!;
      const end = days[Math.min(days.length - 1, c.startIdx + c.count - 1)]!;
      return today >= start && today <= end;
    });
    if (found < 0) return null;
    return LEFT_W + found * colW + colW / 2;
  })();

  const today = startOfDay(new Date());

  return (
    <div className="schedule-gantt" style={cssVars}>
      <div className="schedule-gantt-toolbar">
        <div className="schedule-gantt-toolbar-left">
          <div className="schedule-gantt-view-mode-group" role="group" aria-label="视图模式">
            {(['day', 'week', 'month'] as const).map(m => (
              <button
                key={m}
                type="button"
                className={`schedule-gantt-view-mode-btn${viewMode === m ? ' active' : ''}`}
                onClick={() => {
                  const el = scrollRef.current;
                  if (el) {
                    const maxScroll = el.scrollWidth - el.clientWidth;
                    prevScrollRatio.current = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
                  }
                  setViewMode(m);
                  try {
                    localStorage.setItem(VIEW_MODE_STORAGE_KEY, m);
                  } catch {
                    /* ignore */
                  }
                }}
              >
                {viewModeLabels[m]}
              </button>
            ))}
          </div>
          <select
            id="schedule-gantt-spec-select"
            className="schedule-gantt-spec-select"
            value={selectedSpecId}
            onChange={e => setSelectedSpecId(e.target.value)}
            aria-label="规格"
          >
            {(schedule.specSchedules ?? []).map(spec => (
              <option key={String(spec.id)} value={String(spec.id)}>
                {formatSpecRowTitle(spec)}
              </option>
            ))}
          </select>
          <label className="schedule-gantt-critical-toggle">
            <input
              type="checkbox"
              checked={showCriticalPathOnly}
              onChange={e => setShowCriticalPathOnly(e.target.checked)}
            />
            仅显示关键路径
          </label>
        </div>
        <div className="schedule-gantt-toolbar-right">
          <div className="schedule-gantt-nav-group" role="group" aria-label="时间轴导航">
            <button
              type="button"
              className="schedule-gantt-nav-btn"
              onClick={() => scrollBy(-colW * 7)}
              title="上一段时间"
              aria-label="上一段时间"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="schedule-gantt-range-label">{rangeLabel}</span>
            <button
              type="button"
              className="schedule-gantt-nav-btn"
              onClick={() => scrollBy(colW * 7)}
              title="下一段时间"
              aria-label="下一段时间"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="schedule-gantt-nav-btn"
              onClick={scrollToToday}
              title="回到今天"
              aria-label="回到今天"
            >
              <Locate size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="schedule-gantt-scroll-wrap">
        <div className="schedule-gantt-hscroll" ref={scrollRef}>
          <div className="schedule-gantt-grid" style={{ minWidth: totalWidth }}>
            <div className="schedule-gantt-head" style={{ minWidth: totalWidth }}>
              <div className="schedule-gantt-head-spacer" style={{ width: LEFT_W }}>
                <div className="schedule-gantt-head-left-label">
                  <span>节点名称</span>
                  <span className="schedule-gantt-head-left-sub">计划 / 实际</span>
                </div>
              </div>
              <div className="schedule-gantt-head-tracks" style={{ width: totalCols * colW }}>
                {viewMode === 'day' ? (<>
                  <div className="schedule-gantt-head-months">
                    {monthSegments.map(seg => (
                      <div
                        key={seg.key}
                        className="schedule-gantt-month-cell"
                        style={{ flex: `0 0 ${seg.count * colW}px` } as CSSProperties}
                      >
                        {seg.label}
                      </div>
                    ))}
                  </div>
                  <div className="schedule-gantt-head-weeks">
                    {days.map(d => {
                      const key = format(d, 'yyyy-MM-dd');
                      const nw = isNonWorkingDay(d);
                      return (
                        <div
                          key={`${key}-w`}
                          className={`schedule-gantt-day-col schedule-gantt-day-col-week${nw ? ' non-work' : ''}`}
                          style={{ flex: `0 0 ${colW}px` } as CSSProperties}
                        >
                          <div className="schedule-gantt-day-week">{WEEK_ZH[d.getDay()]}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="schedule-gantt-head-nums">
                    {days.map(d => {
                      const key = format(d, 'yyyy-MM-dd');
                      const nw = isNonWorkingDay(d);
                      return (
                        <div
                          key={`${key}-n`}
                          className={`schedule-gantt-day-col schedule-gantt-day-col-num${nw ? ' non-work' : ''}`}
                          style={{ flex: `0 0 ${colW}px` } as CSSProperties}
                        >
                          <div className="schedule-gantt-day-num">{format(d, 'd')}</div>
                        </div>
                      );
                    })}
                  </div>
                </>) : viewMode === 'week' ? (<>
                  <div className="schedule-gantt-head-months">
                    {monthSegments.map(seg => {
                      const weekCount = weekColumns.filter(w => {
                        const firstDay = days[w.startIdx]!;
                        return format(firstDay, 'yyyy-MM') === seg.key;
                      }).length;
                      if (weekCount === 0) return null;
                      return (
                        <div
                          key={seg.key}
                          className="schedule-gantt-month-cell"
                          style={{ flex: `0 0 ${weekCount * colW}px` } as CSSProperties}
                        >
                          {seg.label}
                        </div>
                      );
                    })}
                  </div>
                  <div className="schedule-gantt-head-weeks">
                    {weekColumns.map(col => (
                      <div
                        key={col.key}
                        className="schedule-gantt-day-col schedule-gantt-day-col-week"
                        style={{ flex: `0 0 ${colW}px` } as CSSProperties}
                      >
                        <div className="schedule-gantt-day-week">{col.label}</div>
                      </div>
                    ))}
                  </div>
                </>) : (<>
                  <div className="schedule-gantt-head-months">
                    {monthColumns.map(col => (
                      <div
                        key={col.key}
                        className="schedule-gantt-month-cell"
                        style={{ flex: `0 0 ${colW}px` } as CSSProperties}
                      >
                        {col.label}
                      </div>
                    ))}
                  </div>
                </>)}
              </div>
            </div>

            <div className="schedule-gantt-body" ref={ganttBodyRef}>
              {layoutRows.map(g => {
                const planBar = g.planBar;
                const actualBar = g.actualBar;
                const hasActualTime = g.hasActual;
                const onCritical = criticalPathMilestoneIds.has(g.milestoneId);
                const timeStatus = getTimeStatus(g, today);
                return (
                  <div
                    key={g.key}
                    className={`schedule-gantt-pair schedule-gantt-pair--status-${g.status}${hasActualTime ? '' : ' no-actual'}`}
                    style={{ minWidth: totalWidth }}
                  >
                    <div className="schedule-gantt-left" style={{ width: LEFT_W }}>
                      <div className="schedule-gantt-left-stack">
                        <div className="schedule-gantt-node-title">
                          {onCritical ? (
                            <Flag size={12} className="schedule-gantt-critical-icon is-critical" aria-label="关键路径" />
                          ) : (
                            <Circle size={12} className="schedule-gantt-critical-icon" aria-label="非关键路径" />
                          )}
                          <span className="schedule-gantt-cell-name" title={g.milestoneName}>
                            {g.milestoneName}
                          </span>
                        </div>

                        {(() => {
                          const planMeta = formatPlanMeta(g.planStart, g.planEnd);
                          const actualMeta = formatActualMeta(g);
                          if (!planMeta && !actualMeta) {
                            return <div className="schedule-gantt-meta-line schedule-gantt-meta-empty">—</div>;
                          }
                          return (
                            <>
                              {planMeta && (
                                <div className="schedule-gantt-meta-line schedule-gantt-meta-plan">{planMeta}</div>
                              )}
                              {actualMeta && (
                                <div className="schedule-gantt-meta-line schedule-gantt-meta-actual">{actualMeta}</div>
                              )}
                            </>
                          );
                        })()}

                        <div className="schedule-gantt-node-badges">
                          <span className={`schedule-gantt-chip schedule-gantt-chip--${g.status}`}>
                            {statusLabel(g.status)}
                          </span>
                          {timeStatus.kind === 'delay' && (
                            <span className="schedule-gantt-chip schedule-gantt-chip--delay">
                              延期{timeStatus.days}天
                            </span>
                          )}
                          {timeStatus.kind === 'early' && (
                            <span className="schedule-gantt-chip schedule-gantt-chip--early">
                              提前{timeStatus.days}天
                            </span>
                          )}
                          {timeStatus.kind === 'normal' && g.status !== 'pending' && (
                            <span className="schedule-gantt-chip schedule-gantt-chip--normal">正常</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="schedule-gantt-right" style={{ width: totalCols * colW }}>
                      <div className="schedule-gantt-timeline-rows">
                        <TimelineRow
                          totalW={totalCols * colW}
                          colW={colW}
                          dayBgs={dayBgs}
                          bar={planBar}
                          variant="plan"
                          status={g.status}
                          planAsGhost={hasActualTime}
                          compact={!hasActualTime}
                          overdue={g.overdue}
                          barRefKey={`${g.specId}:${g.milestoneId}:plan`}
                          onBarRef={setBarRef}
                          draggable={!!onMilestoneDrag}
                          onBarMouseDown={e => handleBarMouseDown(e, g)}
                          dragPreview={dragPreview && dragState.current && dragState.current.specId === Number(g.specId) && dragState.current.milestoneId === Number(g.milestoneId) ? dragPreview : undefined}
                          ghostBar={dragPreview && dragState.current && dragState.current.specId === Number(g.specId) && dragState.current.milestoneId === Number(g.milestoneId) && dragState.current.dragging ? { left: dragState.current.origLeft, width: dragState.current.origWidth } : undefined}
                          dragDateLabel={dragPreview && dragState.current && dragState.current.specId === Number(g.specId) && dragState.current.milestoneId === Number(g.milestoneId) && dragState.current.dragging ? (() => { const s = safeParseDay(dragState.current.startDate); if (!s) return null; const pxDx = dragPreview.left - dragState.current.origLeft; const dd = pixelToDayDelta(pxDx, viewMode, colW, dragState.current.startDate, days, weekColumns, monthColumns); const ns = addDays(s, dd); const ne = addDays(ns, dragState.current.durationDays - 1); return `${format(ns, 'yyyy-MM-dd')} ~ ${format(ne, 'yyyy-MM-dd')}`; })() : undefined}
                        />
                        {hasActualTime && (
                          <TimelineRow
                            totalW={totalCols * colW}
                            colW={colW}
                            dayBgs={dayBgs}
                            bar={actualBar}
                            variant="actual"
                            status={g.status}
                            barRefKey={`${g.specId}:${g.milestoneId}:actual`}
                            onBarRef={setBarRef}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {todayMarkerLeft != null && (
                <div
                  className="schedule-gantt-today-line"
                  style={{ left: todayMarkerLeft }}
                  title="今天"
                />
              )}
              {dragPreview && dragState.current && dragState.current.dragging && (() => {
                const guides: Array<{ left: number; label: string }> = [];
                if (viewMode === 'day') {
                  for (let i = 0; i < days.length; i++) {
                    const d = addDays(timelineStart, i);
                    const px = i * colW + LEFT_W;
                    if (d.getDay() === 1) guides.push({ left: px, label: '周一' });
                    if (d.getDate() === 1) guides.push({ left: px, label: '月初' });
                  }
                } else {
                  const modeCols = viewMode === 'week' ? weekColumns : monthColumns;
                  for (let ci = 0; ci < modeCols.length; ci++) {
                    const col = modeCols[ci]!;
                    const px = ci * colW + LEFT_W;
                    const colDate = days[col.startIdx]!;
                    if (viewMode === 'week') {
                      guides.push({ left: px, label: `W${getISOWeek(colDate)}` });
                    } else if (colDate.getDate() === 1) {
                      guides.push({ left: px, label: format(colDate, 'M月', { locale: zhCN }) });
                    }
                  }
                }
                return guides.map((guide, idx) => (
                  <div
                    key={`guide-${idx}`}
                    className="schedule-gantt-align-guide"
                    style={{
                      position: 'absolute',
                      left: `${guide.left}px`,
                      top: 0,
                      width: 0,
                      height: '100%',
                      borderLeft: '1px dashed rgba(239, 68, 68, 0.5)',
                      pointerEvents: 'none',
                      zIndex: 4,
                    }}
                  />
                ));
              })()}
              <svg
                className="schedule-gantt-dependency-overlay"
                style={{ width: totalWidth, height: overlayHeight }}
              >
                {dependencySegments.map((s, i) => (
                  <g key={`dep-${i}`}>
                    <polyline
                      className={`schedule-gantt-dep-line schedule-gantt-dep-line-${s.kind}`}
                      points={s.points}
                    />
                    <polygon
                      className={`schedule-gantt-dep-arrow schedule-gantt-dep-arrow-${s.kind}`}
                      points={s.arrow}
                    />
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="schedule-gantt-legend">
        <span className="schedule-gantt-legend-label">任务状态</span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--completed" />
          {milestoneStatusLabel('completed')}
        </span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--progress" />
          {milestoneStatusLabel('in_progress')}
        </span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--pending" />
          {milestoneStatusLabel('pending')}
        </span>
        <span className="schedule-gantt-legend-sep" />
        <span className="schedule-gantt-legend-label">时间状态</span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-chip schedule-gantt-chip--normal">正常</span>
        </span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-chip schedule-gantt-chip--delay">延期</span>
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--overdue" />
        </span>
        <span className="schedule-gantt-legend-sep" />
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--ghost" />
          计划时间
        </span>
        <span className="schedule-gantt-legend-item">
          <Flag size={12} className="schedule-gantt-critical-icon is-critical" />
          关键路径
        </span>
        <span className="schedule-gantt-legend-item">
          <span className="schedule-gantt-legend-swatch schedule-gantt-legend-swatch--nonwork" />
          非工作日
        </span>
      </div>

      <div className="schedule-gantt-footer">
        <span className="schedule-gantt-footer-item">
          <List size={12} aria-hidden />
          共 {layoutRows.length} 个里程碑节点
        </span>
        {schedule.createdAt ? (
          <span className="schedule-gantt-footer-item">
            V{schedule.version} · {schedule.name}
          </span>
        ) : null}
      </div>
    </div>
  );
};
